/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from '@/utils/utils';
import { useScene } from '@/lib/useCommon';
import { updateFullScreen, updateShowSubtitle, enterChatPage, enterCardView, CardViewType } from '@/store/slices/room';
import { RootState } from '@/store';
import LoginModal from '@/components/LoginModal';
import LogoFull from '@/assets/img/logo1.svg';
import style from './index.module.less';

function Antechamber() {
  const dispatch = useDispatch();
  const { isScreenMode, isAvatarScene } = useScene();
  const auth = useSelector((state: RootState) => state.auth);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const requireLogin = (): boolean => {
    if (!auth.isAuthenticated) {
      setLoginModalVisible(true);
      return false;
    }
    return true;
  };

  const handleEnterChat = () => {
    if (!requireLogin()) return;
    // 直接进入对话页面，不立即加入房间
    dispatch(updateFullScreen({ isFullScreen: !isMobile() && !isScreenMode && !isAvatarScene }));
    dispatch(updateShowSubtitle({ isShowSubtitle: !isAvatarScene }));
    dispatch(enterChatPage());
  };

  const handleCardClick = (cardType: CardViewType) => {
    if (!cardType) return;
    if (!requireLogin()) return;
    console.log('[Antechamber] 进入卡片视图:', cardType);
    dispatch(enterCardView(cardType));
  };

  return (
    <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
      {/* Logo and Title */}
      <div className={style.header}>
        <div className={style.brandRow}>
          <img src={LogoFull} alt="AbsoluteMind" className={style.logoFull} />
          <div className={style.brandText}>AbsoluteMind</div>
        </div>
        <p className={style.subtitle}>
          先进的AI对话平台，具备实时情绪分析、<br />
          智能预警和长期记忆能力。
        </p>
      </div>

      {/* Action Buttons */}
      <div className={style.actionButtons}>
        <button className={style.primaryButton} onClick={handleEnterChat}>
          <span className={style.buttonIcon}>💬</span>
          开始对话
          <span className={style.buttonArrow}>→</span>
        </button>
        <button className={style.secondaryButton} onClick={() => handleCardClick('emotion-report')}>
          <span className={style.buttonIcon}>📊</span>
          查看仪表盘
        </button>
        <button className={style.secondaryButton} disabled>
          <span className={style.buttonIcon}>🧠</span>
          长期记忆
        </button>
      </div>

      {/* System Capabilities */}
      <div className={style.systemTitle}>系统能力</div>

      {/* Feature Cards - 暂时禁用跳转 */}
      <div className={style.featureCards}>
        <div className={style.featureCard}>
          <div className={style.cardIcon}>🎭</div>
          <h3 className={style.cardTitle}>情绪分析</h3>
          <p className={style.cardDesc}>
            实时情感检测分析您在对话中的情绪状态，提供情绪模式和趋势的深度洞察。
          </p>
        </div>

        <div className={style.featureCard}>
          <div className={style.cardIcon}>⚠️</div>
          <h3 className={style.cardTitle}>预警系统</h3>
          <p className={style.cardDesc}>
            智能监控检测感觉，极端情绪和风险因素，提供及时的警告和知。
          </p>
        </div>

        <div className={style.featureCard}>
          <div className={style.cardIcon}>✨</div>
          <h3 className={style.cardTitle}>长期记忆</h3>
          <p className={style.cardDesc}>
            持久化存储对话中的重要信息，实现跨会话的上下文感知能力。
          </p>
        </div>
      </div>

      <LoginModal
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
      />
    </div>
  );
}

export default Antechamber;
